/**
 * Path: packages/frontend/src/store/filters.ts
 * Task: Manages search queries, filter arrays, and datastore evaluation logic.
 * Update: Fixed crash when searching text fields that are null/undefined.
 */
import { defineStore } from 'pinia';
import { useDatastoreStore } from './datastore';
import { useAuthStore } from './auth';
import type { Entity } from '@/core/types/editor';

export interface FilterQueryRow {
  id: string;
  types: string[];
  instances: string[];
}

export const useFilterStore = defineStore('filters', {
  state: () => ({
    filterQuery: [{ id: 'root', types: [], instances: [] }] as FilterQueryRow[],
    typeFilter: [] as string[],
    statusFilter: [] as string[],
    textFilter: '',
    dynamicFilters: {} as Record<string, string[]>,
    searchQuery: ''
  }),
  getters: {
    entityMatchesFilters: (state) => {
      return (entity: Entity): boolean => {
        const authStore = useAuthStore();
        const { filterQuery, statusFilter, textFilter } = state;

        // 1. Query Builder Logic (Types and Instances)
        if (filterQuery && filterQuery.length > 0) {
          const root = filterQuery[0];

          // Check Type matching
          if (root.types.length > 0 && !root.types.includes(entity.entityType)) {
            return false;
          }

          // Check Instance matching
          if (root.instances.length > 0) {
            if (!root.instances.includes(entity.id)) {
              return false;
            }
          }
        }

        // 2. Relation Builder Logic (Row 2+)
        if (filterQuery && filterQuery.length > 1) {
          for (let i = 1; i < filterQuery.length; i++) {
            const row = filterQuery[i];
            if (row.types.length === 0) continue; // Skip empty relation filters

            let hasRelation = false;

            // Helper to check if entity has ANY relation to the target type/instance
            const checkRelation = (targetType: string, targetInstances: string[]) => {
              for (const key of Object.keys(entity)) {
                if (key === 'history' || key === 'comments') continue;

                const val = entity[key];
                // If it's an array of IDs
                if (Array.isArray(val)) {
                  if (targetInstances.length > 0) {
                    if (val.some(id => targetInstances.includes(id))) return true;
                  } else {
                    if (val.length > 0) return true;
                  }
                }
                // If it's a single ID
                else if (typeof val === 'string' && key.endsWith('Id')) {
                  if (targetInstances.length > 0) {
                    if (targetInstances.includes(val)) return true;
                  } else {
                    if (val) return true;
                  }
                }
              }

              // RACI Check
              if (entity.raci) {
                for (const rKey of Object.keys(entity.raci)) {
                  if (Array.isArray(entity.raci[rKey])) {
                    if (targetInstances.length > 0) {
                      if (entity.raci[rKey].some((id: string) => targetInstances.includes(id))) return true;
                    } else {
                      if (entity.raci[rKey].length > 0) return true;
                    }
                  }
                }
              }

              return false;
            };

            // The entity must have a relation to at least ONE of the specified types in this row
            for (const targetType of row.types) {
              if (checkRelation(targetType, row.instances)) {
                hasRelation = true;
                break;
              }
            }

            if (!hasRelation) return false;
          }
        }

        // 3. Text Filter - Fixed JS Crash on undefined properties
        if (textFilter) {
          const q = textFilter.toLowerCase();
          const tMatch = (entity.title || '').toLowerCase().includes(q);
          const dMatch = (entity.description || '').toLowerCase().includes(q);
          if (!tMatch && !dMatch) return false;
        }

        return true;
      };
    },
    filteredEntities(state): Array<Entity> {
      const datastore = useDatastoreStore();
      if (!datastore.data) return [];
      let entities = datastore.allEntities.filter(e => this.entityMatchesFilters(e));
      return entities.sort((a, b) => {
        const rankDiff = (a.rank || 0) - (b.rank || 0);
        if (rankDiff !== 0) return rankDiff;
        return (a.title || '').localeCompare(b.title || '');
      });
    }
  }
});
